import {NgComponentOutlet, NgTemplateOutlet} from '@angular/common';
import {ChangeDetectionStrategy, Component, computed, effect, inject} from '@angular/core';
import {DialogModule} from 'primeng/dialog';

import {TranslatePipe} from '@mydeals/i18n';

import {ModalSize} from './modal-config.model';
import {ModalService} from './modal-service';

const SIZE_TO_WIDTH: Record<ModalSize, string> = {
  sm: '420px',
  md: '520px',
  lg: '680px',
};

const VIEWPORT_MAX_WIDTH = 'calc(100vw - 2 * var(--space-16))';

@Component({
  selector: 'crm-modal-host',
  imports: [DialogModule, NgComponentOutlet, NgTemplateOutlet, TranslatePipe],
  template: `
    @for (s of activeModal(); track s.ref) {
      <p-dialog
        [visible]="s.ref.visible()"
        [modal]="true"
        [closable]="false"
        [draggable]="false"
        [resizable]="false"
        [dismissableMask]="s.config.dismissible ?? true"
        [closeOnEscape]="s.config.closeOnEscape ?? true"
        [style]="widthStyle()"
        (visibleChange)="onVisibleChange($event)"
      >
        <ng-template pTemplate="headless">
          <div class="modal">
            <div class="modal__head">
              @if (s.config.icon; as icon) {
                <span
                  class="modal__badge"
                  [class.modal__badge--neutral]="s.config.severity === 'neutral'"
                  [class.modal__badge--danger]="s.config.severity === 'danger'"
                  [class.modal__badge--success]="s.config.severity === 'success'"
                >
                  <i [class]="icon"></i>
                </span>
              }
              @if (s.ref.title(); as title) {
                <h2 class="modal__title">{{ title | translate }}</h2>
              }
            </div>

            <div class="modal__body">
              <ng-container *ngComponentOutlet="s.componentType; injector: s.injector" />
            </div>

            @if (s.ref.footer(); as footerTpl) {
              <footer class="modal__footer">
                <ng-container *ngTemplateOutlet="footerTpl" />
              </footer>
            }
          </div>
        </ng-template>
      </p-dialog>
    }
  `,
  styleUrl: './modal-host.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ModalHost {
  private readonly modalService = inject(ModalService);

  readonly state = this.modalService.state;

  // 0-or-1 list so @for can key the dialog by ref: every modal gets a fresh
  // <p-dialog>, so PrimeNG's internal render/motion signals never leak across modals.
  readonly activeModal = computed(() => {
    const s = this.state();
    return s ? [s] : [];
  });

  readonly widthStyle = computed(() => {
    const config = this.state()?.config;
    return {width: config?.width ?? SIZE_TO_WIDTH[config?.size ?? 'sm'], maxWidth: VIEWPORT_MAX_WIDTH};
  });

  constructor() {
    // A new modal mounts hidden; flip it visible next microtask to play the enter animation.
    // Guard against re-showing a modal that was closed before the microtask ran.
    effect(() => {
      const s = this.state();
      if (s && !s.ref.closed) {
        queueMicrotask(() => s.ref.show());
      }
    });
  }

  onVisibleChange(visible: boolean): void {
    if (!visible) this.state()?.ref.close();
  }
}
